import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usePowerSync } from '@powersync/react';
import type { PowerSyncDatabase } from '@powersync/web';
import { useApp, isCloud } from '@/app/state';
import { startDemo } from '@/app/demo';
import { Button, Card, Field, Input } from '@/components/ui';
import { Logo } from '@/components/Logo';
import { setLang, type Lang } from '@/i18n';
import { signInAndUnlock } from '@/features/session/cloud';

export function WelcomePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Lang;
  const db = usePowerSync() as PowerSyncDatabase;
  const { setSession, setCrypto } = useApp();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function demo() {
    setBusy(true);
    try {
      const { session, crypto } = await startDemo(db, lang);
      setCrypto(crypto); setSession(session);
      nav('/reception');
    } finally { setBusy(false); }
  }

  async function signIn(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const r = await signInAndUnlock(db, email, password);
      setCrypto(r.crypto); setSession(r.session);
      nav('/reception');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.failed'));
    } finally { setBusy(false); }
  }

  return (
    <div className={`min-h-dvh flex flex-col items-center justify-center p-6 ${lang === 'my' ? 'font-my' : ''}`}>
      <div className="mb-8 flex items-center gap-3">
        <Logo className="h-12 w-12 text-accent" />
        <div>
          <h1 className="text-3xl font-bold leading-none">Klinixis</h1>
          <p className="text-ink-2">{t('app.tagline')}</p>
        </div>
      </div>
      <div className="grid w-full max-w-3xl gap-4 sm:grid-cols-2">
        {isCloud && (
          <Card>
            <form onSubmit={signIn} className="grid gap-3">
              <h2 className="text-lg font-bold">{t('auth.signIn')}</h2>
              <Field label={t('auth.email')}><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required /></Field>
              <Field label={t('auth.password')} hint={t('auth.unlockBody')}><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required /></Field>
              {error && <p className="text-sm text-danger">{error}</p>}
              <Button variant="primary" type="submit" disabled={busy}>{busy ? t('auth.signingIn') : t('auth.signIn')}</Button>
            </form>
          </Card>
        )}
        <Card>
          <h2 className="text-lg font-bold">{t('auth.demoTitle')}</h2>
          <p className="mb-4 text-ink-2">{t('auth.demoBody')}</p>
          <Button variant={isCloud ? 'secondary' : 'primary'} onClick={demo} disabled={busy}>{t('auth.demoStart')}</Button>
        </Card>
      </div>
      <button className="mt-8 text-sm text-ink-2 underline" onClick={() => setLang(lang === 'my' ? 'en' : 'my')}>{lang === 'my' ? 'English' : 'မြန်မာ'}</button>
    </div>
  );
}
